// qs-InfoModal.vue
<template>
  <div v-if="isVisible" class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      
      <div class="modal-header">
        <qs-Logo :size="48" />
        <div class="app-info">
          <h1 class="app-name">VectorSketch</h1>
          <p class="app-claim">Vector Hand Drawing Made Simple</p>
          <p class="app-by">
            V {{ appVersion }} - by
            <a href="https://anomie.at" target="_blank" rel="noopener noreferrer" class="anomie-link">anomie.at</a>
          </p>
        </div>
      </div>

      
      <div class="app-description">
        <p>Quick freehand vector sketching for Desktop and Tablet to support Adobe Illustrator and Figma workflows. Perfect for rapid ideation and concept development.</p>
      </div>

      
      <div class="features-section">
        <h3 class="section-title">Key Features</h3>
        <ul class="features-list">
          <li><strong>Multiple Brush Types:</strong> Round, Tapering, and specialized brushes for different drawing styles.</li>
          <li><strong>Drag to Change Brush Size:</strong> Hold and drag in the brush size panel to adjust size.</li>
          <li><strong>Layer Management:</strong> Organize your artwork with multiple layers and easy layer controls.</li>
          <li><strong>Export Options:</strong> Save your work as PNG or SVG for maximum compatibility. You can also export the entire workspace as a ZIP and upload it on any device.</li>
        </ul>
      </div>

      
      <div class="shortcuts-section">
        <h3 class="section-title">Keyboard Shortcuts</h3>
        <div class="shortcuts-grid">
          <div class="shortcut-item">
            <span class="key">D</span>
            <span class="description">Delete Mode</span>
          </div>
          <div class="shortcut-item">
            <span class="key">M</span>
            <span class="description">Pan Mode</span>
          </div>
          <div class="shortcut-item shortcut-item--clickable" @click="doPanelReset">
            <span class="key">P</span>
            <span class="description">Panel Reset</span>
          </div>
          <div class="shortcut-item">
            <span class="key">K</span>
            <span class="description">Key Metrics Dashboard</span>
          </div>
          <div class="shortcut-item">
            <span class="key">1-5</span>
            <span class="description">Brush Size Selection</span>
          </div>
          <div class="shortcut-item">
            <span class="key">Esc</span>
            <span class="description">Back to Draw Mode</span>
          </div>
        </div>
      </div>

      <p class="url-hint">You can also reset panels by opening the app with <code>#/panelreset</code> in the URL (e.g. <code>https://your-domain.com/#/panelreset</code>).</p>

      
      <div class="disclaimer">
        <p>⚠️ Use at your own risk. Not for production use. Data is stored locally in your browser only. Please regularly download your SVG files to avoid data loss. Special brushes may impact performance.</p>
      </div>

      
      <div class="modal-footer">
        <button class="close-button" @click="closeModal">Close</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">import qsLogo from '@/components/ui/qs-Logo.vue'
import { useCanvasStore } from '@/stores/qs-store-canvas'
import { usePanelStore } from '@/stores/qs-store-panels'

interface Props {
  isVisible: boolean
}

defineProps<Props>()

const emit = defineEmits<{
  'close': []
}>()

const appVersion = __APP_VERSION__

const panelStore = usePanelStore()
const canvasStore = useCanvasStore()

const doPanelReset = () => {
  panelStore.resetPanelPositions()
  canvasStore.centerArtboard()
}

const closeModal = () => {
  emit('close')
}</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  padding: 20px;
  max-width: 400px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
}

.app-info {
  flex: 1;
}

.app-name {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 2px 0;
}

.app-claim {
  font-size: 12px;
  color: #6b7280;
  margin: 0 0 2px 0;
}

.app-by {
  font-size: 11px;
  color: #9ca3af;
  margin: 0;
}

.anomie-link {
  color: #6b7280;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;
}

.anomie-link:hover {
  color: #374151;
  text-decoration: underline;
}

.app-description {
  margin-bottom: 16px;
}

.app-description p {
  font-size: 12px;
  line-height: 1.4;
  color: #374151;
  margin: 0;
}

.features-section,
.shortcuts-section {
  margin-bottom: 16px;
}

.disclaimer {
  margin-bottom: 12px;
  padding: 8px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 4px;
}

.disclaimer p {
  font-size: 10px;
  line-height: 1.3;
  color: #92400e;
  margin: 0;
  text-align: center;
}

.section-title {
  font-size: 12px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px 0;
}

.features-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.features-list li {
  font-size: 11px;
  line-height: 1.4;
  color: #374151;
  margin-bottom: 6px;
  padding-left: 0;
}

.shortcuts-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.shortcut-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}

.shortcut-item--clickable {
  cursor: pointer;
  border-radius: 4px;
}

.shortcut-item--clickable:hover {
  background: #f3f4f6;
}

.url-hint {
  font-size: 10px;
  line-height: 1.3;
  color: #6b7280;
  margin: 0 0 12px 0;
}

.url-hint code {
  background: #f3f4f6;
  padding: 1px 4px;
  border-radius: 2px;
  font-size: 9px;
}

.key {
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: 600;
  color: #374151;
  min-width: 24px;
  text-align: center;
  font-family: monospace;
}

.description {
  font-size: 11px;
  color: #374151;
  flex: 1;
}

.modal-footer {
  text-align: center;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
}

.close-button {
  background: #6b7280;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 16px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.close-button:hover {
  background: #4b5563;
}
</style>